import React, { useState, useEffect } from 'react';
import {
  useGetAdCategoriesQuery,
  useGetCategoryReportQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import PieChart from '../../../components/charts/PieChart';
import BarChart from '../../../components/charts/BarChart';
import AreaChart from '../../../components/charts/AreaChart';
import { useLanguage } from '../../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faBullhorn,
  faCheckCircle,
  faCalendarPlus,
  faEye,
  faBullseye,
  faComments,
  faChartBar,
  faChartLine,
  faTrophy,
  faTag,
} from '@fortawesome/free-solid-svg-icons';

const PERIOD_OPTIONS = [
  { value: 'last_7_days',  label: 'Last 7 Days'  },
  { value: 'last_30_days', label: 'Last 30 Days'  },
  { value: 'last_90_days', label: 'Last 90 Days'  },
  { value: 'this_year',    label: 'This Year'     },
];

const TYPE_COLORS: Record<string, string> = {
  supply: 'rgba(57,106,84,0.85)',
  demand: 'rgba(248,168,36,0.85)',
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000)     return `${parseFloat((n / 1_000).toFixed(1))}k`;
  return n.toString();
}

interface StatCardProps {
  label: string;
  value: string;
  change?: number | null;
  icon: any;
  iconColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, change, icon, iconColor = '#396A54' }) => (
  <div className="col-6 col-xl-4">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div className="d-flex flex-column gap-1">
            {change !== null && change !== undefined ? (
              <span
                className="badge fw-semibold fs-8 d-inline-flex align-items-center gap-1"
                style={{
                  background: change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: change >= 0 ? '#16a34a' : '#dc2626',
                  width: 'fit-content',
                }}
              >
                <FontAwesomeIcon icon={change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 10 }} />
                {change >= 0 ? '+' : ''}{change}%
              </span>
            ) : (
              <span style={{ height: 22, display: 'block' }} />
            )}
          </div>
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 40, height: 40, background: `${iconColor}18`, flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 18 }} />
          </div>
        </div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: 28, lineHeight: 1 }}>{value}</div>
        <div className="text-muted fw-semibold fs-7">{label}</div>
      </div>
    </div>
  </div>
);

const AdminAdCategoriesReport: React.FC = () => {
  const { t } = useLanguage();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [period,    setPeriod]    = useState('last_30_days');

  const { data: categoriesData } = useGetAdCategoriesQuery({ per_page: 100, status: 1 });
  const categories: any[] = categoriesData?.data?.data || categoriesData?.data || [];

  // Auto-select first category when list loads
  useEffect(() => {
    if (categories.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const reportParams: Record<string, any> = { period };
  if (countryId) reportParams.country_id = countryId;
  if (cityId)    reportParams.city_id    = cityId;

  const { data: reportData, isLoading: reportLoading } = useGetCategoryReportQuery(
    { categoryId: selectedCategoryId!, ...reportParams },
    { skip: selectedCategoryId === null }
  );

  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];

  const d = reportData?.data || {};
  const byType: Array<{ type: string; listing_type: string; count: number }>      = d.by_type || [];
  const monthlyTrend: Array<{ month: string; count: number }>                     = d.monthly_trend || [];
  const topItems: Array<{ name: string; ads: number; views: number; ctr: number; communications: number }> = d.top_items || [];

  const supplyTotal: number = d.supply_ads ?? 0;
  const demandTotal: number = d.demand_ads ?? 0;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Ad Categories Report')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">{t('Home')}</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Reports & Stats')}</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Ad Categories Report')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="d-flex gap-6 align-items-start">

              {/* Left Sidebar */}
              <div
                className="card flex-shrink-0"
                style={{ width: 220, minWidth: 220, position: 'sticky', top: 80 }}
              >
                <div className="card-header min-h-50px px-4 border-bottom">
                  <span className="card-title fw-bold fs-7 text-uppercase text-muted d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faTag} />
                    {t('Categories')}
                  </span>
                </div>
                <div className="card-body p-0" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                  {categories.length === 0 ? (
                    <div className="px-4 py-6 text-muted fs-7">{t('No categories')}</div>
                  ) : (
                    <div className="d-flex flex-column">
                      {categories.map((cat: any) => (
                        <button
                          key={cat.id}
                          className="btn btn-flush px-4 py-3 text-start fs-7 fw-semibold"
                          style={{
                            borderLeft: selectedCategoryId === cat.id
                              ? '3px solid #396A54'
                              : '3px solid transparent',
                            borderRadius: 0,
                            color: selectedCategoryId === cat.id ? '#396A54' : '#5e6278',
                            background: selectedCategoryId === cat.id ? 'rgba(57,106,84,0.06)' : 'transparent',
                            transition: 'all 0.15s',
                          }}
                          onClick={() => setSelectedCategoryId(cat.id)}
                        >
                          {cat.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-grow-1 min-w-0">

                {/* Filter Bar */}
                <div className="card mb-6">
                  <div className="card-body py-5">
                    <div className="row g-4 align-items-end">
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Country')}</label>
                        <select
                          className="form-select form-select-solid"
                          value={countryId}
                          onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
                        >
                          <option value="">{t('All countries')}</option>
                          {countries.map((c: any) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('City')}</label>
                        <select
                          className="form-select form-select-solid"
                          value={cityId}
                          onChange={(e) => setCityId(e.target.value)}
                        >
                          <option value="">{t('All cities')}</option>
                          {cities.map((c: any) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Period')}</label>
                        <select
                          className="form-select form-select-solid"
                          value={period}
                          onChange={(e) => setPeriod(e.target.value)}
                        >
                          {PERIOD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{t(o.label)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedCategoryId === null ? (
                  <div className="card">
                    <div className="card-body d-flex align-items-center justify-content-center py-20">
                      <span className="text-muted fs-6">{t('Select a category from the sidebar to view its stats')}</span>
                    </div>
                  </div>
                ) : reportLoading ? (
                  <PageLoader />
                ) : (
                  <>
                    {/* Category Heading */}
                    <div className="mb-5 d-flex align-items-center gap-3">
                      <h2 className="fw-bold text-dark fs-2 mb-0">{d.category?.title || '—'}</h2>
                    </div>

                    {/* Stat Cards */}
                    <div className="row g-5 mb-6">
                      <StatCard
                        label={t('Total Ads')}
                        value={formatNumber(d.total_ads ?? 0)}
                        icon={faBullhorn}
                        iconColor="#396A54"
                      />
                      <StatCard
                        label={t('Active Ads')}
                        value={formatNumber(d.active_ads ?? 0)}
                        icon={faCheckCircle}
                        iconColor="#22c55e"
                      />
                      <StatCard
                        label={t('New This Period')}
                        value={formatNumber(d.new_in_period ?? 0)}
                        change={d.new_in_period_change ?? null}
                        icon={faCalendarPlus}
                        iconColor="#3b82f6"
                      />
                      <StatCard
                        label={t('Total Views')}
                        value={formatNumber(d.total_views ?? 0)}
                        icon={faEye}
                        iconColor="#8b5cf6"
                      />
                      <StatCard
                        label={t('Average CTR')}
                        value={`${d.avg_ctr ?? 0}%`}
                        icon={faBullseye}
                        iconColor="#ef4444"
                      />
                      <StatCard
                        label={t('Communications')}
                        value={formatNumber(d.communications ?? 0)}
                        change={d.communications_change ?? null}
                        icon={faComments}
                        iconColor="#f59e0b"
                      />
                    </div>

                    {/* Ads by Type + Supply vs Demand */}
                    <div className="card mb-6">
                      <div className="card-header pt-5 border-bottom-0">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faChartBar} style={{ color: '#396A54' }} />
                            {t('Ad Distribution by Type')}
                          </span>
                          <span className="text-muted mt-1 fw-semibold fs-7">
                            {t('Breakdown of ads per sub-type, with supply vs. demand overview')}
                          </span>
                        </h3>
                      </div>
                      <div className="card-body pt-4">
                        <div className="row g-6">

                          {/* By Type Bar Chart */}
                          <div className="col-12 col-xl-8">
                            <div className="border rounded p-6 h-100">
                              <div className="mb-4">
                                <div className="fw-bold text-dark fs-6">{t('Ads per sub-type')}</div>
                                <div className="text-muted fs-7">{t('Number of ads for each category type')}</div>
                              </div>
                              {byType.length > 0 ? (
                                <div style={{ height: 260 }}>
                                  <BarChart
                                    labels={byType.map(bt => bt.type)}
                                    datasets={[
                                      {
                                        label: t('Supply'),
                                        data: byType.map(bt => bt.listing_type === 'supply' ? bt.count : 0),
                                        backgroundColor: TYPE_COLORS.supply,
                                        borderColor: TYPE_COLORS.supply,
                                        borderRadius: 4,
                                      },
                                      {
                                        label: t('Demand'),
                                        data: byType.map(bt => bt.listing_type === 'demand' ? bt.count : 0),
                                        backgroundColor: TYPE_COLORS.demand,
                                        borderColor: TYPE_COLORS.demand,
                                        borderRadius: 4,
                                      },
                                    ]}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      plugins: {
                                        legend: {
                                          position: 'top',
                                          labels: { usePointStyle: true, pointStyle: 'circle', font: { size: 12 } },
                                        },
                                      },
                                      scales: {
                                        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                                        y: {
                                          beginAtZero: true,
                                          grid: { color: 'rgba(0,0,0,0.05)' },
                                          ticks: { font: { size: 11 } },
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                                  <span className="text-muted fs-7">{t('No type data available')}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Supply vs Demand Donut */}
                          <div className="col-12 col-xl-4">
                            <div className="border rounded p-6 h-100">
                              <div className="mb-4">
                                <div className="fw-bold text-dark fs-6">{t('Supply vs. Demand')}</div>
                                <div className="text-muted fs-7">{t('Overall ratio')}</div>
                              </div>
                              {(supplyTotal + demandTotal) > 0 ? (
                                <div className="d-flex flex-column align-items-center">
                                  <div style={{ height: 180, width: '100%' }}>
                                    <PieChart
                                      labels={[t('Supply'), t('Demand')]}
                                      data={[supplyTotal, demandTotal]}
                                      backgroundColor={[TYPE_COLORS.supply, TYPE_COLORS.demand]}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        cutout: '65%',
                                        plugins: { legend: { display: false } },
                                      }}
                                    />
                                  </div>
                                  <div className="d-flex gap-6 mt-4">
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: TYPE_COLORS.supply, flexShrink: 0 }} />
                                      <div>
                                        <div className="fw-bold text-dark fs-7">{formatNumber(supplyTotal)}</div>
                                        <div className="text-muted fs-8">{t('Supply')}</div>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: TYPE_COLORS.demand, flexShrink: 0 }} />
                                      <div>
                                        <div className="fw-bold text-dark fs-7">{formatNumber(demandTotal)}</div>
                                        <div className="text-muted fs-8">{t('Demand')}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
                                  <span className="text-muted fs-7">{t('No data available')}</span>
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>

                    {/* Monthly Trend */}
                    <div className="card mb-6">
                      <div className="card-header pt-5 border-bottom-0">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faChartLine} style={{ color: '#396A54' }} />
                            {t('Monthly Ad Trend')}
                          </span>
                          <span className="text-muted mt-1 fw-semibold fs-7">
                            {t('Number of new ads posted each month over the past 12 months')}
                          </span>
                        </h3>
                      </div>
                      <div className="card-body pt-4">
                        {monthlyTrend.length > 0 ? (
                          <div style={{ height: 280 }}>
                            <AreaChart
                              labels={monthlyTrend.map(m => m.month)}
                              datasets={[
                                {
                                  label: t('New Ads'),
                                  data: monthlyTrend.map(m => m.count),
                                  backgroundColor: 'rgba(57,106,84,0.15)',
                                  borderColor: 'rgba(57,106,84,1)',
                                },
                              ]}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: false },
                                  tooltip: {
                                    callbacks: {
                                      label: (ctx: any) => ` ${ctx.raw} ${t('ads')}`,
                                    },
                                  },
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    grid: { color: 'rgba(0,0,0,0.04)' },
                                    ticks: { font: { size: 11 } },
                                  },
                                  x: {
                                    grid: { display: false },
                                    ticks: { font: { size: 11 } },
                                  },
                                },
                              }}
                            />
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center py-16">
                            <span className="text-muted fs-7">{t('No trend data available')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Top 10 Most Reactive Items */}
                    <div className="card mb-6">
                      <div className="card-header pt-5 border-bottom-0">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faTrophy} style={{ color: '#f6c000' }} />
                            {t('Top 10 Most Reactive Items')}
                          </span>
                          <span className="text-muted mt-1 fw-semibold fs-7">
                            {t('Items generating the highest engagement in this category')}
                          </span>
                        </h3>
                      </div>
                      <div className="card-body pt-2">
                        {topItems.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                              <thead>
                                <tr className="border-0">
                                  <th className="p-0 w-40px text-end text-muted fw-semibold fs-7 text-uppercase">#</th>
                                  <th className="p-0 ps-4 text-muted fw-semibold fs-7 text-uppercase">{t('Item')}</th>
                                  <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Ads')}</th>
                                  <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Views')}</th>
                                  <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('CTR')}</th>
                                  <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Contacts')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {topItems.map((item, i) => (
                                  <tr key={i}>
                                    <td className="text-end pe-4">
                                      <span className="text-dark fw-bold fs-6">{i + 1}</span>
                                    </td>
                                    <td className="ps-4">
                                      <span className="text-dark fw-bold fs-6">{item.name}</span>
                                    </td>
                                    <td>
                                      <span className="text-muted fw-semibold fs-7">{formatNumber(item.ads)}</span>
                                    </td>
                                    <td>
                                      <span className="text-muted fw-semibold fs-7">{formatNumber(item.views)}</span>
                                    </td>
                                    <td>
                                      <span className="text-dark fw-semibold fs-7">{item.ctr}%</span>
                                    </td>
                                    <td>
                                      <span className="text-dark fw-semibold fs-7">{formatNumber(item.communications)}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center py-16">
                            <span className="text-muted fs-7">{t('No item data available')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdCategoriesReport;
